import { getLocaleString } from '@assets/i18n/config';

import type { FinancialProduct } from '@interfaces/financialProduct';
import type { TranslatedString } from '@interfaces/shared';
import type {
  ForeignExchangeRate,
  Holding,
  Statistics,
} from '@interfaces/stats';
import { AccountTypes } from '@onevesthq/ov-enums';
import Decimal from 'decimal.js';
import {
  AMOUNT_TYPES,
  DEFAULT_RESULT_CURRENCY,
  EMPTY_RESULT_PLACEHOLDER,
  REDEEM_MODES,
} from './calculator.constants';
import type {
  AmountType,
  CalculatorBodyPayload,
  FundLineForm,
  IntegrationResult,
  RedeemMode,
  RedemptionCalculatorResponse,
  RedemptionFundLinePayload,
} from './calculator.types';

const AMOUNT_TYPE_VALUES = new Set<AmountType>([
  AMOUNT_TYPES.ALL_SHARES,
  AMOUNT_TYPES.SHARES,
  AMOUNT_TYPES.DOLLAR_GROSS,
  AMOUNT_TYPES.DOLLAR_NET,
  AMOUNT_TYPES.ANNUAL_FREE_ALLOCATION,
  AMOUNT_TYPES.DSC_FREE,
]);

const REDEEM_MODE_VALUES = new Set<RedeemMode>([
  REDEEM_MODES.SINGLE,
  REDEEM_MODES.MULTIPLE,
  REDEEM_MODES.ENTIRE,
]);

export const isCalculatorFieldEnabled = (field: unknown) => {
  if (field === true) return true;
  if (field && typeof field === 'object' && 'enabled' in field) {
    return Boolean((field as { enabled: boolean }).enabled);
  }
  return false;
};

export const getCalculatorLabel = (
  translateBackendString: (translatedObject: unknown) => string,
  options: Record<string, unknown>,
  fieldKey: string,
  fallback: string,
) => {
  const value = options[fieldKey];
  const inputLabel = translateBackendString(
    (value as { label?: unknown })?.label,
  );
  if (inputLabel) return inputLabel;

  if (value && typeof value === 'object' && 'customLabelsTemp' in value) {
    const customLabelsTemp = (
      value as {
        customLabelsTemp?: Record<
          string,
          TranslatedString & { enabled?: boolean }
        >;
      }
    ).customLabelsTemp;
    const labelKey = `${fieldKey}Label`;
    const customLabel = customLabelsTemp?.[labelKey];
    if (customLabel?.enabled) {
      const translated = translateBackendString(customLabel);
      if (translated) return translated;
    }
  }

  return fallback;
};

export const getCalculatorFundDisplayLabel = (
  translateBackendString: (translatedObject: unknown) => string,
  financialProduct?: Pick<FinancialProduct, 'ticker' | 'translatedName'> | null,
): string => {
  const name = translateBackendString(financialProduct?.translatedName);
  const ticker = financialProduct?.ticker?.trim() ?? '';

  if (!name) return ticker;
  if (!ticker) return name;

  const tickerSuffix = `(${ticker})`;
  return `${name} ${tickerSuffix}`;
};

export const shouldHideWithholdingTax = (accountType?: AccountTypes) =>
  accountType === AccountTypes.PERSONAL || accountType === AccountTypes.TFSA;

const CURRENCY_RESULT_FIELDS = new Set([
  'unitPrice',
  'tenPercentAmount',
  'marketValue',
  'grossRedemption',
  'backEndRedemptionFee',
  'grossProceeds',
  'withholdingTax',
  'netAmount',
]);

const TEXT_RESULT_FIELDS = new Set(['currency']);

const DATE_RESULT_FIELDS = new Set(['priceDate']);

const PERCENT_RESULT_FIELDS = new Set(['backEndFeePercentage']);

export const parsePositiveAmount = (amount?: string) => {
  if (!amount?.trim()) return undefined;

  try {
    const parsed = new Decimal(amount.trim());
    if (!parsed.isFinite() || parsed.lte(0)) return undefined;
    return parsed;
  } catch {
    return undefined;
  }
};

export const formatCalculatorCurrency = (value: number) => {
  const locale = getLocaleString();
  const symbolAfter = !locale.startsWith('en');
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }).format(value);
  return symbolAfter ? `${formatted} $` : `$${formatted}`;
};

export const formatCalculatorPercent = (value: number) => {
  const locale = getLocaleString();
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
  return `${formatted}%`;
};

export const formatCalculatorResultValue = (
  fieldKey: string,
  value?: string | number | null,
  formatDate?: (date: string) => string,
) => {
  if (value == null || value === '') return EMPTY_RESULT_PLACEHOLDER;

  if (TEXT_RESULT_FIELDS.has(fieldKey)) {
    return String(value);
  }

  if (DATE_RESULT_FIELDS.has(fieldKey)) {
    return formatDate ? formatDate(String(value)) : String(value);
  }

  if (PERCENT_RESULT_FIELDS.has(fieldKey)) {
    return formatCalculatorPercent(Number(value));
  }

  if (CURRENCY_RESULT_FIELDS.has(fieldKey)) {
    return formatCalculatorCurrency(Number(value));
  }

  return String(value);
};

export const getDefaultResultCurrency = (availableCurrencies: string[]) =>
  availableCurrencies[0] ?? DEFAULT_RESULT_CURRENCY;

export const parseCalculatorBody = (
  body: unknown,
): CalculatorBodyPayload | undefined => {
  if (!body || typeof body !== 'object') return undefined;

  const candidate = body as Record<string, unknown>;
  if (typeof candidate.accountId !== 'string') return undefined;
  if (typeof candidate.resultCurrency !== 'string') return undefined;
  if (
    typeof candidate.redeemMode !== 'string' ||
    !REDEEM_MODE_VALUES.has(candidate.redeemMode as RedeemMode)
  ) {
    return undefined;
  }
  if (!Array.isArray(candidate.input) || candidate.input.length === 0) {
    return undefined;
  }

  const input = candidate.input.flatMap((line) => {
    if (!line || typeof line !== 'object') return [];
    const entry = line as Record<string, unknown>;
    if (typeof entry.fundCode !== 'string') return [];
    if (!AMOUNT_TYPE_VALUES.has(entry.amountType as AmountType)) return [];

    return [
      {
        fundCode: entry.fundCode,
        amountType: entry.amountType as AmountType,
        ...(typeof entry.amount === 'number' ? { amount: entry.amount } : {}),
      },
    ];
  });

  if (!input.length) return undefined;

  return {
    accountId: candidate.accountId,
    resultCurrency: candidate.resultCurrency,
    redeemMode: candidate.redeemMode as RedeemMode,
    input,
  };
};

export const createEmptyFundLine = (): FundLineForm => ({
  fundCode: '',
  amountType: '',
});

export const resolveRedeemMode = (
  redeemMode: RedeemMode,
  holdingsCount: number,
) => {
  if (redeemMode === REDEEM_MODES.ENTIRE && holdingsCount === 0) {
    return REDEEM_MODES.SINGLE;
  }
  if (redeemMode === REDEEM_MODES.MULTIPLE && holdingsCount < 2) {
    return REDEEM_MODES.SINGLE;
  }
  return redeemMode;
};

export const canRestorePersistedIntegrationResults = ({
  redeemMode,
  input,
  holdings,
}: {
  redeemMode: RedeemMode;
  input: RedemptionFundLinePayload[];
  holdings: Holding[];
}) => {
  if (holdings.length === 0) return false;
  if (!input.length) return false;

  const validFundCodes = new Set(
    holdings
      .map((h) => h.financialProduct?.id)
      .filter((id): id is string => Boolean(id)),
  );
  const inputFundCodes = new Set(input.map((line) => line.fundCode));

  if (redeemMode === REDEEM_MODES.ENTIRE) {
    for (const code of validFundCodes) {
      if (!inputFundCodes.has(code)) return false;
    }
    for (const code of inputFundCodes) {
      if (!validFundCodes.has(code)) return false;
    }
    return true;
  }

  return input.every((line) => validFundCodes.has(line.fundCode));
};

export const mapBodyInputToFundLines = (
  input: RedemptionFundLinePayload[],
  holdings: Holding[],
): FundLineForm[] => {
  const validFundCodes = new Set(
    holdings.map((holding) => holding.financialProduct?.id).filter(Boolean),
  );

  const lines = input
    .filter((line) => validFundCodes.has(line.fundCode))
    .map((line) => ({
      fundCode: line.fundCode,
      amountType: line.amountType,
      amount: line.amount != null ? String(line.amount) : undefined,
    }));

  return lines.length ? lines : [createEmptyFundLine()];
};

export const getAvailableCurrencies = (statistics?: Statistics) => {
  const rates = statistics?.foreignExchangeRates ?? [];
  if (!rates.length) {
    return ['CAD'];
  }
  const from = rates[0]?.from ?? 'CAD';
  const to = rates[0]?.to ?? 'USD';
  return [from, to].filter(Boolean);
};

export type FundLineAmountError = 'sharesExceed' | 'dollarExceed';

const convertDollars = (
  dollars: Decimal,
  fromCurrency: string,
  toCurrency: string,
  foreignExchangeRates: ForeignExchangeRate[],
): Decimal => {
  if (fromCurrency === toCurrency) return dollars;

  const directRate = foreignExchangeRates.find(
    (rate) => rate.from === fromCurrency && rate.to === toCurrency,
  )?.rate;
  if (directRate) return dollars.mul(directRate);

  const inverseRate = foreignExchangeRates.find(
    (rate) => rate.from === toCurrency && rate.to === fromCurrency,
  )?.rate;
  if (inverseRate) return dollars.div(inverseRate);

  return dollars;
};

const getHoldingMarketValueDollars = (
  holding: Holding,
  resultCurrency: string,
  foreignExchangeRates: ForeignExchangeRate[],
): Decimal | undefined => {
  if (holding.totalCents == null) return undefined;

  const dollars = new Decimal(holding.totalCents).div(100);
  const holdingCurrency = holding.currency ?? DEFAULT_RESULT_CURRENCY;

  return convertDollars(
    dollars,
    holdingCurrency,
    resultCurrency,
    foreignExchangeRates,
  );
};

export const getFundLineAmountError = (
  line: FundLineForm,
  holdings: Holding[],
  resultCurrency: string,
  foreignExchangeRates: ForeignExchangeRate[],
): FundLineAmountError | undefined => {
  if (
    line.amountType !== AMOUNT_TYPES.SHARES &&
    line.amountType !== AMOUNT_TYPES.DOLLAR_GROSS &&
    line.amountType !== AMOUNT_TYPES.DOLLAR_NET
  ) {
    return undefined;
  }

  const amount = parsePositiveAmount(line.amount);
  if (!amount) return undefined;

  const holding = holdings.find(
    (h) => h.financialProduct?.id === line.fundCode,
  );
  if (!holding) return undefined;

  if (line.amountType === AMOUNT_TYPES.SHARES) {
    return holding.quantity != null && amount.gt(holding.quantity)
      ? 'sharesExceed'
      : undefined;
  }

  const maxDollars = getHoldingMarketValueDollars(
    holding,
    resultCurrency,
    foreignExchangeRates,
  );
  return maxDollars != null && amount.gt(maxDollars)
    ? 'dollarExceed'
    : undefined;
};

export const validateFundLineAmount = (
  line: FundLineForm,
  holdings: Holding[],
  resultCurrency: string,
  foreignExchangeRates: ForeignExchangeRate[],
) => {
  if (
    line.amountType === AMOUNT_TYPES.SHARES ||
    line.amountType === AMOUNT_TYPES.DOLLAR_GROSS ||
    line.amountType === AMOUNT_TYPES.DOLLAR_NET
  ) {
    if (parsePositiveAmount(line.amount) === undefined) return false;
    return (
      getFundLineAmountError(
        line,
        holdings,
        resultCurrency,
        foreignExchangeRates,
      ) === undefined
    );
  }
  return true;
};

export const validateCalculatorForm = ({
  accountId,
  foreignExchangeRates,
  fundLines,
  holdings,
  redeemMode,
  resultCurrency,
}: {
  accountId?: string;
  foreignExchangeRates: ForeignExchangeRate[];
  fundLines: FundLineForm[];
  holdings: Holding[];
  redeemMode: RedeemMode | '';
  resultCurrency: string;
}) => {
  if (!accountId) return false;
  if (!redeemMode) return false;

  if (redeemMode === REDEEM_MODES.ENTIRE) {
    return (
      holdings.map((h) => h.financialProduct?.id).filter(Boolean).length > 0
    );
  }

  const effectiveLines =
    redeemMode === REDEEM_MODES.SINGLE ? fundLines.slice(0, 1) : fundLines;

  if (!effectiveLines.length) return false;

  return effectiveLines.every((line) => {
    if (!line.fundCode || !line.amountType) return false;
    return validateFundLineAmount(
      line,
      holdings,
      resultCurrency,
      foreignExchangeRates,
    );
  });
};

export const buildCalculatorPayload = ({
  accountId,
  resultCurrency,
  redeemMode,
  fundLines,
  holdings,
}: {
  accountId: string;
  resultCurrency: string;
  redeemMode: RedeemMode;
  fundLines: FundLineForm[];
  holdings: Holding[];
}): CalculatorBodyPayload => {
  if (redeemMode === REDEEM_MODES.ENTIRE) {
    return {
      accountId,
      resultCurrency,
      redeemMode,
      input: holdings.flatMap((holding) => {
        const fundCode = holding.financialProduct?.id;
        if (!fundCode) return [];
        return [{ fundCode, amountType: AMOUNT_TYPES.ALL_SHARES }];
      }),
    };
  }

  const effectiveLines =
    redeemMode === REDEEM_MODES.SINGLE ? fundLines.slice(0, 1) : fundLines;

  return {
    accountId,
    resultCurrency,
    redeemMode,
    input: effectiveLines.map((line) => {
      const entry: {
        fundCode: string;
        amountType: AmountType;
        amount?: number;
      } = {
        fundCode: line.fundCode,
        amountType: line.amountType as AmountType,
      };

      if (
        line.amountType === AMOUNT_TYPES.SHARES ||
        line.amountType === AMOUNT_TYPES.DOLLAR_GROSS ||
        line.amountType === AMOUNT_TYPES.DOLLAR_NET
      ) {
        entry.amount = parsePositiveAmount(line.amount)?.toNumber();
      }

      return entry;
    }),
  };
};

export const parseIntegrationResult = (context: {
  integrationResult?: {
    status: 'success' | 'error';
    data?: unknown;
    error?: { code?: string; message?: string };
  };
}): IntegrationResult | undefined => {
  const integrationResult = context.integrationResult;
  if (!integrationResult) {
    return undefined;
  }

  if (integrationResult.status === 'success') {
    return {
      status: 'success',
      data: integrationResult.data as RedemptionCalculatorResponse | undefined,
    };
  }

  return {
    status: 'error',
    error: {
      message: integrationResult.error?.message ?? '',
    },
  };
};
